import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  WritableSignal,
  computed,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { OrderService } from '../services/order.service';
import { IconNameType } from '../../shared/icons/icon-name-type.enum';
import { LayoutService } from '../services/layout.service';
import { OrderedItemService } from '../services/ordering/ordered-item.service';
import { Subscription, tap } from 'rxjs';
import { Router } from '@angular/router';
import { StoreIdService } from '../services/store-id.service';
import { ConfigService } from '../services/config.service';
import { IconComponent } from "../../shared/icons/icon.component";
import { CommonModule } from '@angular/common';
import { AsyncButtonDirective } from '../../shared/directives/async-button.directive';
import { SpecialInstructionsAccordionComponent } from '../../shared/special-instructions-accordion/special-instructions-accordion.component';
import { ChoicesComponent } from '../item-editor/choices/choices.component';
import { OrderSpecialService } from '../services/ordering/order-special.service';
import { ItemEditorHeaderComponent } from "../item-editor/item-editor-header/item-editor-header.component";
import { SpecialWizardService } from '../services/special-wizard.service';
import { trigger, style, transition, animate } from '@angular/animations';
import { SpecialItemsListComponent } from "./special-items-list/special-items-list.component";
import { OrderedItem } from '../models/ordering/ordered-item.model';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { SpecialItemEditorStepsComponent } from "./special-item-editor-steps/special-item-editor-steps.component";
import { OrderableItem } from '../models/menu/orderable-item.model';
import { ModalName, ModalService } from '../services/modal.service';
import { ScrollToTopComponent } from "../../shared/scroll-to-top/scroll-to-top.component";
import { GTMService } from '../services/gtm.service';
import { EcommerceTriggeringArea } from '../models/ecommerce/ecommerce-triggering-area.enum';
import { EcommerceTriggeringPoint } from '../models/ecommerce/ecommerce-triggering-point.enum';
import { AuditService } from '../services/audit.service';


interface DetailsItem {
  title: string;
  body?: string;
}

enum ContentType {
  editor,
  itemList,
  steps,
}

@Component({
  selector: 'cwo-special-item-editor',
  standalone: true,
  imports: [
    ChoicesComponent,
    IconComponent,
    AsyncButtonDirective,
    SpecialInstructionsAccordionComponent,
    CommonModule,
    ItemEditorHeaderComponent,
    SpecialItemsListComponent,
    TranslocoPipe,
    SpecialItemEditorStepsComponent,
    ScrollToTopComponent
],
  animations: [
    trigger(
      'enterAnimation', [
      transition(':enter', [
        style({ transform: 'translateX(-80%)', opacity: 0 }),
        animate('300ms 300ms', style({ transform: 'translateX(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ transform: 'translateX(0)', opacity: 1 }),
        animate('300ms', style({ transform: 'translateX(-80%)', opacity: 0 }))
      ])
    ]
    )
  ],
  templateUrl: './special-item-editor.component.html',
  styleUrl: './special-item-editor.component.scss'
})

export class SpecialItemEditorComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly header = viewChild<ItemEditorHeaderComponent>('header');
  readonly specialStepsCompRef = viewChild<SpecialItemEditorStepsComponent>('specialStepsRef');
  readonly itemEditorScrollableWrapper = viewChild.required<ElementRef>('itemEditorScrollableWrapper');

  orderService = inject(OrderService);
  layoutService = inject(LayoutService);
  modalService = inject(ModalService);
  orderedItemService = inject(OrderedItemService);
  orderSpecialService = inject(OrderSpecialService);
  specialWizardService = inject(SpecialWizardService);
  router = inject(Router);
  storeIdService = inject(StoreIdService);
  configService = inject(ConfigService);
  translocoService = inject(TranslocoService);
  gtmService = inject(GTMService);
  private auditService = inject(AuditService);

  contentType = ContentType;
  iconNameType = IconNameType;
  toggleWarningsAfterSubmission = false;
  firstInvalidChoiceAfterSubmission = '';
  loadingSubscription: Subscription | null = null;
  itemInstructions: string = '';
  isMultiStep = false;
  selectedItems: OrderedItem[] = [];
  animationDisabled: boolean = true;
  footerError: string | undefined;
  showUnmetRequirementsError = false;
  buttonText?: string;
  currentContentType = signal(ContentType.editor);
  isDirectEdit = false;
  showItemNameOnSpecialSingeStep = false;

  tempSelectedOrderable: WritableSignal<OrderableItem | undefined> = signal(undefined);
  tempSelectedOrderedItem: WritableSignal<OrderedItem | undefined> = signal(undefined);

  highlightInvalidFields = signal(false);

  //SPECIAL WIZARD COMPUTED PARAMS
  specialWizard = this.specialWizardService.editingSpecialWizard;
  orderableSpecial = computed(() => this.specialWizardService.editingSpecialWizard()?.orderableSpecial);
  picture = computed(() => (this.tempSelectedOrderable()?.pictures ?? [null])[0] ?? (this.orderableSpecial()?.pictures ?? [null])[0] ?? null);
  specialName = computed(() => this.orderableSpecial()?.label ?? this.orderableSpecial()?.name ?? '');
  title = computed(() => this.currentContentType() === ContentType.editor && this.isCurrentStepMultiItem() ? ((this.tempSelectedOrderable()?.label ?? this.tempSelectedOrderable()?.name) ?? this.specialName()) : (this.specialName()));
  showItemDescription = computed(() => !!this.tempSelectedOrderable()?.description && !!this.configService.configState().store?.itemWebDescriptionInEditor);

  
  detailItems = computed<DetailsItem[]>(() => {
    const details = <DetailsItem[]>[];
    if (this.orderableSpecial()?.description) {
      details.push({
        title: this.translocoService.translate('modals.special_item_editor.details.description'),
        body: this.orderableSpecial()?.description
      });
    } else if (!this.showItemDescription() && this.orderableSpecial()?.instructions) {
      details.push({
        title: this.translocoService.translate('modals.special_item_editor.details.instructions'),
        body: this.orderableSpecial()?.instructions
      });
    }
    if (this.orderableSpecial()?.disclaimer) {
      details.push({
        title: this.translocoService.translate('modals.special_item_editor.details.disclaimer'),
        body: this.orderableSpecial()?.disclaimer
      });
    }
    return details;
  })


  showBackButton = computed(() => {
    return this.isMultiStep ? this.currentContentType() !== ContentType.steps : (this.isCurrentStepMultiItem() && this.currentContentType() === ContentType.editor);
  });
  
  isCurrentStepMultiItem = computed(() => {
    return (this.specialWizard()?.currentStep?.allItems.length ?? 0) > 1;
  });

  currentItemSurcharge = computed<number | undefined>(() => {
    const currentStep = this.specialWizard()?.currentStep;
    const label = this.tempSelectedOrderable()?.label;
    const size = this.tempSelectedOrderedItem()?.size;
    const matchingItem = currentStep?.allItems.find(item => item.label === label);
    return matchingItem?.getSurchargeForSize(size);
  });

  isEmptyBody = computed(() => {
    return this.tempSelectedOrderable()?.choices.length === 0 && (!this.orderService.hasMultipleSizePrices(this.tempSelectedOrderable()) || this.isMultiStep);
  });

  ngOnInit(): void {
    this.buttonText = this.translocoService.translate('modals.special_item_editor.buttons.add');
    this.itemInstructions = this.tempSelectedOrderable()?.instructions ?? '';

    if (this.specialWizard()?.currentStep && this.specialWizard()?.steps.length === 1) {
      if (this.specialWizard()?.currentStep?.selectedOrderedItem()) {
        this.tempSelectedOrderable.set(this.specialWizard()?.currentStep?.selectedOrderableItem()?.copy());
        this.tempSelectedOrderedItem.set(this.specialWizard()?.currentStep?.selectedOrderedItem()?.copy());
        this.showItemNameOnSpecialSingeStep = true;
        this.currentContentType.set(ContentType.editor);
      } else {
        this.currentContentType.set(ContentType.itemList);
      }
    } else {
      this.currentContentType.set(ContentType.steps);
      this.isMultiStep = true;
    }    
    setTimeout(() => {
      const stateForModal = this.modalService.stateForModal(ModalName.SpecialItemEditor);
      this.gtmService.pushViewPromotionEvent(
        this.specialName(),
        this.orderableSpecial()?.code ?? '',
        stateForModal?.triggeringArea,
        stateForModal?.triggeringPoint
      );
    })
  }

  ngAfterViewInit() {
    const modalContent = document.getElementById(
      'item-editor-scrollable-wrapper'
    );
    const footer = document.getElementById('item-editor-footer');

    if (modalContent && footer) {
      modalContent.style.paddingBottom = `${footer.getBoundingClientRect().height
        }px`;
    }
    setTimeout(() => {
      this.animationDisabled = false;
    });
  }

  ngOnDestroy(): void {
    this.specialWizardService.destroyState();
    this.orderedItemService.resetPrice();
    this.loadingSubscription?.unsubscribe();
  }

  cancelModal() {
    this.modalService.closeModal(ModalName.SpecialItemEditor, { canceled: true });
  }

  onItemSelected(event:{orderableItem: OrderableItem, orderedItem: OrderedItem}) {


    if(event?.orderableItem?.isEditable){
      this.updateEditorObjects(event.orderableItem, event.orderedItem);
      this.currentContentType.set(ContentType.editor);
      this.footerError = undefined;
    } else {
      this.specialWizard()?.currentStep?.selectedOrderedItem.set(event.orderedItem);
      this.specialWizard()?.currentStep?.selectedOrderableItem.set(event.orderableItem);
      this.currentContentType.set(ContentType.steps);
      this.footerError = undefined;
    }
    setTimeout(() => {
      document.getElementById('item-editor-scrollable-wrapper')?.scroll({ top: 0, behavior: 'instant' });
    }, 0);
  }

  updateEditorObjects(orderableItem?: OrderableItem, orderedItem?: OrderedItem) {
    this.tempSelectedOrderable.set(orderableItem ?? this.specialWizardService.editingSpecialWizard()?.currentStep?.selectedOrderableItem()?.copy());
    this.tempSelectedOrderedItem.set(orderedItem ?? this.specialWizardService.editingSpecialWizard()?.currentStep?.selectedOrderedItem()?.copy());
  }


  openImageViewer() {
    this.modalService.openModal(ModalName.ImagePreview, {
      imageSrc: this.picture()?.src ?? ''
    })
  }

  back() {
    this.auditService.createAudit(() => "Back button clicked");
    this.footerError = undefined;
    this.showUnmetRequirementsError = false;
    this.toggleWarningsAfterSubmission = false;

    if (this.currentContentType() === ContentType.editor) {
      this.tempSelectedOrderable.set(undefined);
      this.tempSelectedOrderedItem.set(undefined);
      if (this.isCurrentStepMultiItem() && !this.isDirectEdit) {
        this.currentContentType.set(ContentType.itemList);
      } else {
        this.currentContentType.set(ContentType.steps);
        this.isDirectEdit = false;
      }
    } else if( this.currentContentType() === ContentType.itemList) {
      this.currentContentType.set(ContentType.steps);
    }

  }

  addItemInstructions() {
    const maxInstructionsLength = this.configService.configState().store?.maxSpecialInstructionsLength;
    const orderedItem = this.tempSelectedOrderedItem()?.copy();

    if (this.itemInstructions.length <= maxInstructionsLength && this.itemInstructions.length > 0) {
      orderedItem.instructions = this.itemInstructions;
    }

    return orderedItem;
  }

  _validateSingleStepAndProceed() {
    if (this.currentContentType() === ContentType.itemList) {
      if (this.selectedItems.length === 0) {
        this.footerError = this.translocoService.translate('modals.special_item_editor.errors.no_item_selected');
        return false;
      } else {
        this.footerError = undefined;
        return false;
      }
    }

    const fallbackOrdered = this.specialWizard()?.currentStep?.selectedOrderedItem();
    const fallbackOrderable = this.specialWizard()?.currentStep?.selectedOrderableItem();

    const currentOrderedItem  = this.tempSelectedOrderedItem() ?? fallbackOrdered;
    const currentOrderableItem  = this.tempSelectedOrderable() ?? fallbackOrderable;

    if (!currentOrderedItem  || !currentOrderableItem ) {
      return false;
    }

    if (this.orderedItemService.isValid(currentOrderedItem , currentOrderableItem )) {
      this.specialWizard()?.currentStep?.selectedOrderedItem.set(currentOrderedItem .copy());
      this.specialWizard()?.currentStep?.selectedOrderableItem.set(currentOrderableItem .copy());
      return true;
    } else {
      this.showChoiceWarnings();
      return false;
    }
  }

  handleFooterActionButton() {
    this.toggleWarningsAfterSubmission = false;

    if(this.isMultiStep) {
      if(this._validateMultiStepAndProceed() && this.currentContentType() !== ContentType.editor) this.addSpecialToOrder();
    } else {
      if(this._validateSingleStepAndProceed()) this.addSpecialToOrder();
      this.auditService.createAudit(() => `Special ${this.title()} has been added to order`);
    }
  }

  addSpecialToOrder() {
    const special = this.specialWizardService.getOrderedSpeialFromWizard();
    if (!special) {
      return;
    }

    if (!special.items || special.items.length === 0) {
      const currentStepItem = this.specialWizardService.editingSpecialWizard()?.currentStep?.selectedOrderedItem();
      if (currentStepItem) {
        special.items = [currentStepItem.copy()];
      } else if (this.tempSelectedOrderedItem()) {
        special.items = [this.tempSelectedOrderedItem()!.copy()];
      }
    }

    //quantity is always 1 when adding from the special wizard remove minimum quantity if item has one
    if (special?.items && Array.isArray(special.items)) {
      special.items.forEach((item: OrderedItem) => {
        item.quantity = 1;
      });
    }
  
    this.loadingSubscription = this.orderSpecialService
      .addSpecialToOrder(special)
      .pipe(
        tap((orderedSpecial) => {
          this.gtmService.pushAddToCartEvent({
            order: this.orderService.order(),
            triggeringArea: EcommerceTriggeringArea.SPECIAL_WIZARD,
            triggeringPoint: EcommerceTriggeringPoint.ORDER_BUTTON,
            specialOrdinal: orderedSpecial.ordinal,
            specialName: orderedSpecial.special,
          });
        }),
      )
      .subscribe({
        error: () => {
          this.loadingSubscription = null;
          if(this.header()?.detailsExpanded){
            document.getElementById('item-editor-scrollable-wrapper')?.scroll({ top: 16.8 * 16, behavior: 'smooth' });
          } else {
            document.getElementById('item-editor-scrollable-wrapper')?.scroll({ top: 0, behavior: 'smooth' });
          }
          this.showUnmetRequirementsError = true;
        },
        complete: () => {
          this.loadingSubscription = null;
          this.modalService.closeModal(ModalName.SpecialItemEditor).subscribe({
            complete: () => {
              if (!this.router.url.includes(`/${this.storeIdService.getStorePathPrefix()}/checkout`)) {
                this.modalService.openModal(ModalName.OrderTreeNotification, { special: special });
              }
            }
          });
          
        },
      });
  }

  private _validateMultiStepAndProceed() {
    if(this.currentContentType() === ContentType.steps){
      this.auditService.createAudit(() => "Special Wizard's ADD TO ORDER button clicked");
      if(this.specialWizard()?.isComplete){
        return true;
      } else {
        const firstInvalidIndex = this.specialWizard()?.steps.findIndex(step => !step.isFulfilled);
        this.auditService.createAudit(() => `Step ${firstInvalidIndex! + 1} with name ${this.specialWizard()?.orderableSpecial?.steps[firstInvalidIndex!].title} is required`);
        this.highlightInvalidFields.set(firstInvalidIndex !== -1);
        this.specialStepsCompRef()?.focusStep(firstInvalidIndex ?? -1)
        return false;
      }
    } else {  /* Only other contentType should be the 'Editor' since on the list we hide the button*/
      if(this.tempSelectedOrderedItem()?.isValid()){
        this.toggleWarningsAfterSubmission = false;
        this.specialWizard()?.currentStep?.markedAsDone.set(true);
        this.addNoticePeriodToOrderedItem();
        this.specialWizard()?.currentStep?.selectedOrderedItem.set(this.tempSelectedOrderedItem()?.copy());
        this.specialWizard()?.currentStep?.selectedOrderableItem.set(this.tempSelectedOrderable()?.copy());
        this.auditService.createAudit(() => `Selected ${this.tempSelectedOrderedItem()?.size} ${this.tempSelectedOrderedItem()?.item} `);
        this.currentContentType.set(ContentType.steps);
        this.tempSelectedOrderable.set(undefined);
        this.tempSelectedOrderedItem.set(undefined);
        this.isDirectEdit = false;
        return false;
      } else {
        this.showChoiceWarnings();
        return false;
      }
    }
   
  }

  private addNoticePeriodToOrderedItem()  {
    const orderedItem = this.tempSelectedOrderedItem()?.copy();
    const sizePrice = this.tempSelectedOrderable()?.sizePrices?.find(sp => sp.size === this.tempSelectedOrderedItem()?.size);
      const noticePeriod = sizePrice?.noticePeriod && sizePrice.noticePeriod > 0
        ? sizePrice.noticePeriod
        : undefined;

    orderedItem.noticePeriod = noticePeriod;

    this.tempSelectedOrderedItem.set(orderedItem);
  }

  viewDetails(){    
    this.header()?.updateExpanded(true);
    document.getElementById('item-editor-scrollable-wrapper')?.scroll({ top: 16.8 * 16, behavior: 'smooth' });
  }

  resetWarningsAfterSubmissionValue() {
    this.firstInvalidChoiceAfterSubmission = '';
  }

  onInstructionsChange(value: string): void {
    this.itemInstructions = value;
  }

  handleChangeStep(index: number) {
    this.specialWizard()?.setCurrentStepIndex(index);
    this.currentContentType.set(ContentType.itemList);
    if (this.specialWizard()?.steps[index].isFulfilled) {
      this.auditService.createAudit(() => "Shuffle button clicked");
    }
    else {
      this.auditService.createAudit(() => `Step ${index+1} with name ${this.orderableSpecial()?.steps[index].title} button clicked`);
    }
  }

  handleEdit(index: number){
    this.auditService.createAudit(() => `Edit for Step: ${index+1} with name ${this.orderableSpecial()?.steps[index].title} button clicked`);
    this.specialWizardService.editingSpecialWizard()?.setCurrentStepIndex(index);
    this.isDirectEdit = true;
    this.updateEditorObjects();
    this.currentContentType.set(ContentType.editor);
  }

  showChoiceWarnings() {
    this.toggleWarningsAfterSubmission = true;
    const firstInvalidChoice = this.tempSelectedOrderedItem()?.choices.find(
      (c) => c.warnings?.length
    );
    this.firstInvalidChoiceAfterSubmission = firstInvalidChoice?.name ?? '';
  }

  updateTempItemFromChoices(item: OrderedItem){
    this.tempSelectedOrderedItem.set(item?.copy());
  }
}
